import { Link as RouterLink } from 'expo-router';
import { File as FileIcon, X } from 'lucide-react-native';
import { memo, useCallback } from 'react';

import { ConfirmationCheckbox } from '@/components/form/ConfirmationCheckbox';
import { Card } from '@/components/ui/card';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { LinkText } from '@/components/ui/link';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { useChatAnalytics } from '@/hooks/chat/useChatAnalytics';
import type { InquiryParticipant } from '@/hooks/useChat';
import type { UploadFile } from '@/types/uploadFile';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { getNativeTextProps } from '@/util/getNativeTextProps';

function FileUpload({
  file,
  removeFile,
  isSending,
}: {
  file: UploadFile;
  removeFile: (file: UploadFile) => void;
  isSending: boolean;
}) {
  return (
    <HStack className="min-w-[140px] max-w-full items-center gap-1.5 rounded-md bg-background-50 p-1.5">
      <Icon as={FileIcon} size="xs" className="flex-shrink-0 text-typography-900" />

      <Text
        className="min-w-0 flex-1 font-medium text-xs text-typography-900"
        {...getNativeTextProps()}>
        {file.name}
      </Text>

      <Pressable
        onPress={() => removeFile(file)}
        disabled={isSending}
        {...getAccessibilityProps({ label: `Remove attachment ${file.name}`, role: 'button' })}
        hitSlop={{
          top: 16,
          bottom: 16,
          left: 16,
          right: 16,
        }}
        className="flex-shrink-0 rounded-sm p-1.5 hover:bg-background-100 active:bg-background-200 disabled:opacity-50">
        <Icon as={X} size="md" className="text-typography-900" />
      </Pressable>
    </HStack>
  );
}

type ChatContentFooterProps = {
  closedConversation: boolean;
  isAuthor: boolean;
  recipient: InquiryParticipant;
  files: UploadFile[];
  setFiles: React.Dispatch<React.SetStateAction<UploadFile[]>>;
  isSending: boolean;
  noPHIConfirmed: boolean;
  setNoPHIConfirmed: (value: boolean) => void;
};

/**
 * Chat footer that handles the closed-conversation banner, file uploads
 * with removal analytics, and PHI confirmation.
 */
export const ChatContentFooter = memo(function ChatContentFooter({
  closedConversation,
  isAuthor,
  recipient,
  files,
  setFiles,
  isSending,
  noPHIConfirmed,
  setNoPHIConfirmed,
}: ChatContentFooterProps) {
  const { track } = useChatAnalytics();

  const handleRemoveFile = useCallback(
    (file: UploadFile) => {
      setFiles((prev) => {
        track(ANALYTICS_EVENTS.ATTACHMENTS_REMOVED, {
          file_name: file.name,
          file_size: file.size,
          mime_type: file.mimeType,
          remaining_file_count: prev.length - 1,
          context: 'chat',
        });

        const newFiles = prev.filter((f) => f.uri !== file.uri);

        if (newFiles.length === 0) {
          setNoPHIConfirmed(false);
        }

        return newFiles;
      });
    },
    [setFiles, setNoPHIConfirmed, track]
  );

  if (closedConversation) {
    return (
      <Card className="items-center" variant="filled" size="md">
        <Text size="sm" className="mb-0.5">
          This conversation has been closed.
        </Text>
        {isAuthor ? (
          <RouterLink
            href={{ pathname: '/directory/inquiry/[id]', params: { id: recipient.id } }}
            {...getAccessibilityProps({
              label: 'Start a new conversation with this expert',
              role: 'link',
            })}>
            <LinkText className="text-primary-500 data-[hover=true]:text-primary-600" size="sm">
              Start a new conversation with this expert
            </LinkText>
          </RouterLink>
        ) : null}
      </Card>
    );
  }

  if (files?.length) {
    return (
      <VStack className="mb-3 gap-y-3">
        <HStack className="flex-wrap gap-1.5">
          {files.map((file) => (
            <FileUpload
              key={file.uri}
              file={file}
              removeFile={handleRemoveFile}
              isSending={isSending}
            />
          ))}
        </HStack>

        <ConfirmationCheckbox
          isChecked={noPHIConfirmed || false}
          onChange={setNoPHIConfirmed}
          isDisabled={isSending}
          variant="phi"
        />
      </VStack>
    );
  }

  return null;
});
